import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import MicIcon from '@mui/icons-material/Mic';
import { useTheme, Dialog, Box, IconButton } from '@mui/material';
import Recorder from './Recorder';
import Timer from 'react-compound-timer';
import { StartComponent } from './StartComponent';
import { PlayComponent } from './PlayComponent';
import { RecComponent } from './RecComponent';
import CloseIcon from '@mui/icons-material/Close';
import { translate } from 'translations/translator';

const getTranslation = key => translate('lessonAssignment', key);

type Props = {
  maxRecordTime: number;
  onSendMessage: (audioMessage: File) => void;
  teacherInstructions: boolean;
  buttonContent?: React.ReactNode;
};

const MicRecorderButton = ({ maxRecordTime, teacherInstructions, ...props }: Props) => {
  const [showRec, setShowRec] = useState<boolean>(false);
  const [recorder, setRecorder] = useState<any>(undefined);
  const [recState, setRecState] = useState<string>('start');
  const [audioMessage, setAudioMessage] = useState<any>(undefined);
  const [stopRecTimeout, setStopRecTimeout] = useState<any>(undefined);

  const theme = useTheme();
  const classes = useStyles(theme)();

  const Timer1 = Timer as any;

  const onShowRecorder = () => {
    setShowRec(true);
  };

  const handleStartRecording = async (startTimer, stopTimer) => {
    const newRecorder = new Recorder();
    setRecorder(newRecorder);

    await newRecorder
      ?.setup()
      ?.then(() => {
        setRecState('rec');
        startTimer();
        // stop recording  after 2 minutes
        setStopRecTimeout(setTimeout(() => handleStopRecording(stopTimer), maxRecordTime * 1000));
      })
      .catch(handleRecorderInitError);
  };

  const readFile = (buffer, blob) => {
    const newAudioMessage = new File(buffer, `recording_${Date.now()}.mp3`, {
      type: blob.type,
      lastModified: Date.now()
    });

    setAudioMessage(newAudioMessage);

    if (!newAudioMessage || newAudioMessage.size === 0) {
      handleRecorderInitError(undefined);
    }
  };

  const handleRecorderInitError = error => {
    console.log(error);
    setAudioMessage(undefined);
    setRecState('start');
  };

  const handleStopRecording = async stopTimer => {
    stopTimer();
    if (stopRecTimeout) {
      clearTimeout(stopRecTimeout);
      setStopRecTimeout(undefined);
    }
    const [buffer, blob] = await recorder.stop().getMp3().catch(handleRecorderInitError);
    readFile(buffer, blob);
    setRecorder(undefined);
    setRecState('play');
  };

  const handleDelete = resetTimer => {
    resetTimer();
    setAudioMessage(undefined);
    setRecState('start');
  };

  const handleSend = async resetTimer => {
    resetTimer();
    await props.onSendMessage(audioMessage);
    setRecState('start');
    setAudioMessage(undefined);
    setShowRec(false);
  };

  const handleClose = (resetTimer, stop) => {
    if (recState === 'rec' && stopRecTimeout) {
      clearTimeout(stopRecTimeout);
      setStopRecTimeout(undefined);
    }
    stop();
    resetTimer();
    setRecState('start');
    setAudioMessage(undefined);
    setShowRec(false);
    setRecorder(undefined);
  };

  return (
    <>
      {props.buttonContent ? (
        <Box onClick={onShowRecorder}>{props.buttonContent}</Box>
      ) : (
        <Box mr={2} border={1} borderRadius={8} onClick={onShowRecorder}>
          <IconButton
            {...props}
            size="small"
            color="inherit"
            sx={{
              borderRadius: 8
            }}
          >
            <MicIcon />
          </IconButton>
        </Box>
      )}
      <Timer1
        initialTime={maxRecordTime}
        startImmediately={false}
        formatValue={value => (value < 10 ? `0${value}` : `${value}`)}
      >
        {({ start, stop, reset }) => (
          <Dialog onClose={() => handleClose(reset, stop)} open={showRec}>
            <Box className={classes.header}>
              <Box component="span" className={classes.title}>
                {getTranslation('recordAudio')}
              </Box>
              <CloseIcon className={classes.closeIcon} onClick={() => handleClose(reset, stop)} />
            </Box>
            {recState === 'start' && (
              <StartComponent
                onStart={() => handleStartRecording(start, stop)}
                teacherInstructions={teacherInstructions}
              />
            )}
            <Box>
              {recState === 'rec' && (
                <RecComponent
                  Timer={
                    <>
                      <Timer1.Minutes />:<Timer1.Seconds /> / 02:00
                    </>
                  }
                  onStopRecording={() => handleStopRecording(stop)}
                  teacherInstructions={teacherInstructions}
                />
              )}
              {recState === 'play' && (
                <PlayComponent
                  audioMessage={audioMessage}
                  onDelete={() => handleDelete(reset)}
                  onSend={() => handleSend(reset)}
                  teacherInstructions={teacherInstructions}
                />
              )}
            </Box>
          </Dialog>
        )}
      </Timer1>
    </>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    header: () => ({
      backgroundColor: theme.palette.primary.main,
      width: '800px',
      height: '66px',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      padding: '0px 24px'
    }),
    title: () => ({
      color: theme.palette.black.light,
      fontSize: '28px',
      flexGrow: 1,
      fontWeight: 500
    }),
    closeIcon: () => ({
      color: theme.palette.black.main,
      fontSize: '22px',
      '&:hover': {
        cursor: 'pointer'
      }
    })
  }));

export default MicRecorderButton;
